import React, { useState, useCallback, useEffect, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import { fileStorageService } from 'api/services/fileStorageService';
import type { DineItemPhoto } from 'api/types';

/**
 * The Photos tab of the item dialog (spec 8.10b).
 *
 * Every action here saves at once — there is no Save button for photos. An
 * upload is a request; a caption is saved when the field loses focus; the
 * cover and remove buttons act on click. The API answers every mutation with
 * the dish's full list, because the cover can move as a side effect (removing
 * the cover promotes the next), so the list is always replaced wholesale.
 *
 * Pictures are shown through fileStorageService.openAsObjectUrl: a plain
 * <img src> cannot send the tenant headers, so the bytes are fetched through
 * axios and handed over as blob: URLs, revoked when they are no longer shown.
 */
interface Props {
  itemId: string;
}

const MAX_PER_UPLOAD = 10;
const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic';

export const DineOpsPhotoPanel: React.FC<Props> = ({ itemId }) => {
  const [photos, setPhotos] = useState<DineItemPhoto[]>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [captions, setCaptions] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<DineItemPhoto | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const urlsRef = useRef<Record<string, string>>({});

  /** Replaces the list, fetching bytes only for photos not already shown. */
  const show = useCallback(async (list: DineItemPhoto[]) => {
    setPhotos(list);
    setCaptions(Object.fromEntries(list.map((p) => [p.id, p.caption ?? ''])));

    const next: Record<string, string> = {};
    for (const p of list) {
      if (urlsRef.current[p.id]) {
        next[p.id] = urlsRef.current[p.id];
      } else if (/^https?:\/\//i.test(p.imageUrl)) {
        // Sample data carries absolute CDN links; a browser can load those directly.
        next[p.id] = p.imageUrl;
      } else {
        try { next[p.id] = await fileStorageService.openAsObjectUrl(p.imageUrl); } catch { /* shown as a blank tile */ }
      }
    }
    // Revoke what is no longer on screen; a removed photo must not leak its blob.
    for (const [id, url] of Object.entries(urlsRef.current)) {
      if (!next[id] && url.startsWith('blob:')) URL.revokeObjectURL(url);
    }
    urlsRef.current = next;
    setUrls(next);
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    dineOpsService.getItemPhotos(itemId)
      .then((list) => { if (alive) return show(list); })
      .catch(() => notificationService.error('Could not load', 'The photos could not be loaded. Try again.'))
      .finally(() => { if (alive) setLoading(false); });

    return () => {
      alive = false;
      Object.values(urlsRef.current).forEach((u) => { if (u.startsWith('blob:')) URL.revokeObjectURL(u); });
      urlsRef.current = {};
    };
  }, [itemId, show]);

  const upload = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const list = Array.from(files);
    if (list.length > MAX_PER_UPLOAD) {
      notificationService.error('Too many at once', `Choose at most ${MAX_PER_UPLOAD} photos in one go.`);
      return;
    }
    setBusy(true);
    try {
      await show(await dineOpsService.uploadItemPhotos(itemId, list));
      notificationService.success('Added', `${list.length} photo${list.length === 1 ? '' : 's'} added.`);
    } catch (err: any) {
      notificationService.error('Not added', err?.response?.data?.message ?? 'The photos could not be uploaded.');
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }, [itemId, show]);

  const saveCaption = useCallback(async (p: DineItemPhoto) => {
    const caption = (captions[p.id] ?? '').trim();
    if (caption === (p.caption ?? '')) return;  // nothing changed: no round trip
    setBusy(true);
    try {
      await show(await dineOpsService.updateItemPhoto(itemId, p.id, {
        caption: caption || null, isCover: p.isCover, displayOrder: p.displayOrder, rowVersion: p.rowVersion,
      }));
    } catch (err: any) {
      notificationService.error('Not saved', err?.response?.data?.message ?? 'The caption could not be saved.');
    } finally {
      setBusy(false);
    }
  }, [itemId, captions, show]);

  const makeCover = useCallback(async (p: DineItemPhoto) => {
    setBusy(true);
    try {
      await show(await dineOpsService.updateItemPhoto(itemId, p.id, {
        caption: p.caption ?? null, isCover: true, displayOrder: p.displayOrder, rowVersion: p.rowVersion,
      }));
    } catch (err: any) {
      notificationService.error('Not saved', err?.response?.data?.message ?? 'The cover could not be changed.');
    } finally {
      setBusy(false);
    }
  }, [itemId, show]);

  const remove = useCallback(async () => {
    const p = removing;
    if (!p) return;
    setRemoving(null);
    setBusy(true);
    try {
      await show(await dineOpsService.deleteItemPhoto(itemId, p.id, p.rowVersion));
      notificationService.success('Removed', p.isCover ? 'The cover was removed; the next photo is the cover now.' : 'Photo removed.');
    } catch (err: any) {
      notificationService.error('Not removed', err?.response?.data?.message ?? 'The photo could not be removed.');
    } finally {
      setBusy(false);
    }
  }, [itemId, removing, show]);

  return (
    <>
      <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 10 }}>
        Photos are for the guest view — the screen the waiter turns round. They never print on a
        KOT or a bill. One photo is the <strong>cover</strong>; it is what a list shows.
        A dish with no photos is normal.
      </div>

      <input
        ref={fileInput}
        type="file"
        accept={ACCEPT}
        multiple
        style={{ display: 'none' }}
        onChange={(e) => void upload(e.target.files)}
      />
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        disabled={busy || loading}
        onClick={() => fileInput.current?.click()}
      >
        <IconSystem.add size={14} /> {busy ? 'Working…' : 'Add photos'}
      </button>

      {loading && <div style={{ marginTop: 12, opacity: 0.7 }}>Loading…</div>}

      {!loading && photos.length === 0 && (
        <div style={{ marginTop: 12, opacity: 0.7 }}>No photos yet. Add one or more — the first becomes the cover.</div>
      )}

      {photos.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 12,
            marginTop: 12,
          }}
        >
          {photos.map((p) => (
            <div
              key={p.id}
              style={{
                border: p.isCover ? '2px solid var(--color-primary, #2563eb)' : '1px solid var(--border-color, #ddd)',
                borderRadius: 8,
                padding: 8,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <div
                style={{
                  position: 'relative',
                  aspectRatio: '4 / 3',
                  background: '#f3f4f6',
                  borderRadius: 6,
                  overflow: 'hidden',
                }}
              >
                {urls[p.id] ? (
                  <img
                    src={urls[p.id]}
                    alt={p.caption || 'Dish photo'}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', opacity: 0.5 }}>
                    <IconSystem.info size={20} />
                  </div>
                )}
                {p.isCover && (
                  <span
                    style={{
                      position: 'absolute', top: 6, left: 6,
                      background: 'var(--color-primary, #2563eb)', color: '#fff',
                      fontSize: 11, padding: '2px 6px', borderRadius: 4,
                    }}
                  >
                    Cover
                  </span>
                )}
              </div>

              <input
                type="text"
                className="form-input"
                placeholder="Caption (optional)"
                maxLength={200}
                value={captions[p.id] ?? ''}
                disabled={busy}
                onChange={(e) => setCaptions((c) => ({ ...c, [p.id]: e.target.value }))}
                onBlur={() => void saveCaption(p)}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
              />

              <div style={{ display: 'flex', gap: 6, justifyContent: 'space-between' }}>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={busy || p.isCover}
                  title={p.isCover ? 'This is the cover' : 'Show this one in lists'}
                  onClick={() => void makeCover(p)}
                >
                  {p.isCover ? 'Cover' : 'Make cover'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={busy}
                  title="Remove this photo"
                  onClick={() => setRemoving(p)}
                >
                  <IconSystem.trash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={removing !== null}
        title="Remove this photo?"
        message={
          removing?.isCover
            ? 'This is the cover. The next photo will become the cover.'
            : 'The photo is removed from the dish. It cannot be undone.'
        }
        confirmLabel="Remove"
        cancelLabel="Keep"
        variant="danger"
        onConfirm={() => void remove()}
        onCancel={() => setRemoving(null)}
      />
    </>
  );
};

export default DineOpsPhotoPanel;
